import { atom } from 'jotai'
import { atomFamily, atomWithStorage } from 'jotai/utils'
import { atomWithHash } from 'jotai-location'
import { ChatMessage, ChatSettings } from '@/types'
import { Tables } from '@/supabase/types'
import { getStorageForKey } from '@/lib/storage/storage-config'

// ===== Input state atoms =====
// Use atomWithStorage for persistence and better performance
export const userInputAtom = atomWithStorage('userInput', '', {
  getItem: (key) => {
    try {
      const value = localStorage.getItem(key)
      return value ? JSON.parse(value) : ''
    } catch {
      return ''
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value))
    } catch {
      // Ignore storage errors
    }
  },
  removeItem: (key) => {
    try {
      localStorage.removeItem(key)
    } catch {
      // Ignore storage errors
    }
  }
})

export const isTypingAtom = atom(false)
export const inputFocusAtom = atom(true)

// ===== Chat message state atoms =====
export const chatMessagesAtom = atomFamily((chatId: string) => atom<ChatMessage[]>([]))

// Atom setter for updating chat messages in an atom family
export const setChatMessagesAtom = atom(
  null,
  (get, set, params: { chatId: string; messages: ChatMessage[] }) => {
    const { chatId, messages } = params
    if (!chatId) return
    set(chatMessagesAtom(chatId), messages)
  }
)

export const selectedChatIdAtom = atomWithHash<string | null>('chatId', null)

export const selectedChatAtom = atom<Tables<'chats'> | null>(null)

// Derived atom for current chat messages
export const currentChatMessagesAtom = atom((get) => {
  const chatId = get(selectedChatIdAtom)
  if (!chatId) return []
  return get(chatMessagesAtom(chatId as string))
})

// ===== Chat settings atoms =====
const defaultChatSettings: ChatSettings = {
  model: 'losi-omega',
  prompt: 'You are a helpful AI assistant.',
  temperature: 0.5,
  contextLength: 100000,
  includeProfileContext: true,
  includeWorkspaceInstructions: true,
  embeddingsProvider: 'openai',
  reflectMode: false
}

export const chatSettingsAtom = atomWithStorage<ChatSettings>(
  'chatSettings',
  defaultChatSettings,
  getStorageForKey('chatSettings')
)

// ===== Message generation state atoms =====
export const isGeneratingAtom = atom(false)
export const isImageGeneratingAtom = atom(false)
export const firstTokenReceivedAtom = atom(false)
export const abortControllerAtom = atom<AbortController | null>(null)

// ===== Performance metrics atoms =====
export const requestTokensTotalAtom = atom(0)
export const responseTokensTotalAtom = atom(0)
export const responseTimeToFirstTokenAtom = atom(0)
export const responseTimeTotalAtom = atom(0)

// ===== Chat files and attachments atoms =====
export const chatFilesAtom = atom<Tables<'file_items'>[]>([])
export const newMessageFilesAtom = atom<any[]>([])
export const chatImagesAtom = atom<any[]>([])
export const newMessageImagesAtom = atom<any[]>([])
export const showFilesDisplayAtom = atom(false)

// ===== Chat command atoms =====
export const slashCommandAtom = atom('')
export const hashtagCommandAtom = atom('')
export const toolCommandAtom = atom('')
export const atCommandAtom = atom('')

// ===== UI state for pickers/modals =====
export const isPromptPickerOpenAtom = atom(false)
export const isFilePickerOpenAtom = atom(false)
export const isToolPickerOpenAtom = atom(false)
export const isAssistantPickerOpenAtom = atom(false)
export const isMessageHistoryPickerOpenAtom = atom(false)

// ===== Focus state atoms =====
export const focusPromptAtom = atom(false)
export const focusFileAtom = atom(false)
export const focusToolAtom = atom(false)
export const focusAssistantAtom = atom(false)

// ===== Message selection atoms =====
export const selectedMessagesAtom = atom<string[]>([])
export const isSelectingAtom = atom(false)

// ===== New chat state =====
export const isNewChatAtom = atom(false)

// Chat id currently in Losi Inference realtime. Survives /chat → /chat/[id]
// remounts (unlike useState inside useChatHandler). Null = text mode.
export const realtimeActiveChatIdAtom = atom<string | null>(null)

// ===== Utility functions =====

// Helper to select a chat and load its messages - updated for proper usage with atoms
export const selectChat = (chatId: string, chat: Tables<'chats'>, messages: ChatMessage[]) => {
  return {
    selectedChatId: chatId,
    selectedChat: chat,
    chatMessages: messages
  }
}

// Helper to add a message to the current chat - updated for proper usage with atoms
export const addMessageToCurrentChat = (
  message: ChatMessage,
  chatId: string,
  currentMessages: ChatMessage[]
) => {
  return [...currentMessages, message]
}

// Helper to update a message in the current chat - updated for proper usage with atoms
export const updateMessageInCurrentChat = (
  messageId: string,
  updatedMessage: Partial<ChatMessage>,
  currentMessages: ChatMessage[]
) => {
  return currentMessages.map((msg) =>
    msg.message.id === messageId
      ? { ...msg, message: { ...msg.message, ...updatedMessage.message } }
      : msg
  )
}

// Reset generation state - updated for proper usage with atoms
export interface GenerationState {
  isGenerating: boolean
  firstTokenReceived: boolean
  abortController: AbortController | null
  requestTokensTotal: number
  responseTokensTotal: number
  responseTimeToFirstToken: number
  responseTimeTotal: number
}

export const resetGenerationState = (): GenerationState => {
  return {
    isGenerating: false,
    firstTokenReceived: false,
    abortController: null,
    requestTokensTotal: 0,
    responseTokensTotal: 0,
    responseTimeToFirstToken: 0,
    responseTimeTotal: 0
  }
}
